// =============================================================================
// PEDRA — src/components/Modal.tsx
// Modal acessivel generico: role="dialog", aria-modal, foco preso dentro da
// caixa, ESC fecha, clique no fundo fecha, foco devolvido a quem abriu e
// rolagem do body travada enquanto aberto.
//
// NAO usa portal: renderiza no lugar onde foi montado. O posicionamento vem do
// CSS (`position: fixed` + `z-index: var(--z-modal)` em `modal.css`).
// =============================================================================

import { useCallback, useEffect, useId, useRef } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
} from 'react';

export interface ModalProps {
  open: boolean;
  title: string;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Fecha ao clicar no fundo. Default true. */
  closeOnBackdrop?: boolean;
}

/**
 * Seletor dos elementos que podem receber foco por Tab. Mantido em um unico
 * lugar para que o "focus trap" e o foco inicial usem exatamente a mesma regra.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** true quando o elemento esta realmente visivel e alcancavel pelo teclado. */
function isReachable(el: HTMLElement): boolean {
  if (el.hasAttribute('hidden')) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  if (el.closest('[inert]') !== null) return false;
  // Em jsdom `offsetParent` e sempre null, entao so descartamos o que o
  // proprio elemento declara como escondido.
  const style = el.style;
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  return true;
}

/** Lista, em ordem de documento, os focaveis de dentro do container. */
function focusableWithin(root: HTMLElement): HTMLElement[] {
  const found = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return found.filter(isReachable);
}

export function Modal(props: ModalProps): ReactElement | null {
  const {
    open,
    title,
    onClose,
    children,
    footer,
    size = 'md',
    closeOnBackdrop = true,
  } = props;

  const dialogRef = useRef<HTMLDivElement | null>(null);
  /** Elemento que tinha o foco antes de abrir — recebe o foco de volta. */
  const openerRef = useRef<HTMLElement | null>(null);
  /** true quando o mousedown comecou no proprio fundo (evita fechar ao arrastar). */
  const backdropPressRef = useRef(false);
  const titleId = useId();

  // --- Trava a rolagem do body, guarda e restaura o foco -------------------
  useEffect(() => {
    if (!open) return;

    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement ? active : null;

    const body = document.body;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener && document.contains(opener)) {
        // Devolve o foco de forma segura: se o botao sumiu, nao faz nada.
        try {
          opener.focus({ preventScroll: true });
        } catch {
          /* elemento nao focavel — ignorar */
        }
      }
    };
  }, [open]);

  // --- Foco inicial dentro da caixa ---------------------------------------
  useEffect(() => {
    if (!open) return;
    const box = dialogRef.current;
    if (!box) return;
    const targets = focusableWithin(box);
    const first = targets[0];
    if (first) first.focus({ preventScroll: true });
    else box.focus({ preventScroll: true });
  }, [open]);

  // --- ESC + Tab circular --------------------------------------------------
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const box = dialogRef.current;
      if (!box) return;
      const targets = focusableWithin(box);
      if (targets.length === 0) {
        event.preventDefault();
        box.focus({ preventScroll: true });
        return;
      }

      const first = targets[0];
      const last = targets[targets.length - 1];
      if (!first || !last) return;
      const current = document.activeElement;

      if (event.shiftKey) {
        if (current === first || !box.contains(current)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
        return;
      }
      if (current === last || !box.contains(current)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    },
    [onClose],
  );

  // --- Clique no fundo -----------------------------------------------------
  const handleBackdropMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      backdropPressRef.current = event.target === event.currentTarget;
    },
    [],
  );

  const handleBackdropClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!closeOnBackdrop) return;
      if (event.target !== event.currentTarget) return;
      if (!backdropPressRef.current) return;
      backdropPressRef.current = false;
      onClose();
    },
    [closeOnBackdrop, onClose],
  );

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className={`modal-box modal-box--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <header className="modal-header">
          <h2 className="modal-title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Fechar"
            title="Fechar (Esc)"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="modal-body">{children}</div>

        {footer ? <footer className="modal-footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

export default Modal;
